import _ from 'lodash';

import {DEFAULT_LINE_PLOT_MAX_GROUP_RUNS} from '../../components/WorkspaceDrawer/Settings/runLinePlots/maxRunDefaults';
import {
  type QueryToRunsDataQueryParams,
  type RunsDataQueryConfig,
  toRunsDataQueryConfig,
} from '../../containers/RunsDataLoader';
import {SmoothingType} from '../../generated/graphql';
import {
  type BucketedHistorySpec,
  type HistorySpec,
  type MetricTransformation,
} from '../../state/runs/types';
import {CHART_SAMPLES} from '../../util/constants';
import {type Expression, metricsInExpression} from '../../util/expr';
import * as Filters from '../../util/filters';
import {type Filter} from '../../util/filterTypes';
import * as PanelHelpers from '../../util/panelHelpers';
import {type Query} from '../../util/queryTypes';
import {type PrimaryLineType} from '../../util/rampFeatureFlags/linePlotDynamicConfigs';
import {isAnyRunsetGrouped} from '../../util/runhelpers';
import {keyPossibilities, shouldAddDotValue} from '../../util/runs';
import {type Key} from '../../util/runTypes';
import {DEFAULT_X_AXIS_SETTINGS} from '../WorkspaceDrawer/Settings/runLinePlots/linePlotDefaults';
import {areOutliersExcluded} from '../WorkspaceDrawer/Settings/utils';
import {isSystemMetric} from './../../util/metrics';
import {MULTI_METRIC_MAX_RUNS} from './defaults';
import {getExtraFields} from './queryConstructor/getSectionFields';
import {type RunsLinePlotConfig} from './types';

export interface Range {
  min: number | null;
  max: number | null;
}

function getMetricTransformations(
  runsLinePlotConfig: RunsLinePlotConfig,
  metricKey: string
): {
  [key: string]: MetricTransformation[];
} {
  if (
    runsLinePlotConfig.metrics != null &&
    runsLinePlotConfig.smoothingWeight &&
    runsLinePlotConfig.smoothingWeight > 0
  ) {
    let transformation: MetricTransformation;
    if (runsLinePlotConfig.smoothingType === SmoothingType.Exponential) {
      // Clamp smoothing weight to be between 0 and 1
      const clampedSmoothingWeight = Math.min(
        0.99,
        runsLinePlotConfig.smoothingWeight
      );
      transformation = {
        type: 'ema',
        parameters: {
          alpha: clampedSmoothingWeight,
          zeroDebias: true,
        },
      };
    } else if (runsLinePlotConfig.smoothingType === SmoothingType.Gaussian) {
      transformation = {
        type: 'gaussian',
        parameters: {
          sigma: runsLinePlotConfig.smoothingWeight,
        },
      };
    } else if (runsLinePlotConfig.smoothingType === SmoothingType.Average) {
      transformation = {
        type: 'running_average',
        parameters: {
          windowSize: runsLinePlotConfig.smoothingWeight,
        },
      };
    } else if (
      runsLinePlotConfig.smoothingType ===
        SmoothingType.ExponentialTimeWeighted ||
      runsLinePlotConfig.smoothingType === undefined
    ) {
      transformation = {
        type: 'time_weighted_ema',
        parameters: {
          alpha: runsLinePlotConfig.smoothingWeight,
        },
      };
    } else {
      return {};
    }

    return {[metricKey]: [transformation]};
  }

  return {};
}

export function runsLinePlotTransformQuery(options: {
  defaultMaxRuns: number;
  dynamicConfigGroupingDepth?: number;
  enableBackendSmoothing?: boolean;
  isFullFidelity?: boolean;
  parsedExpressions: {
    expressions: Expression[] | undefined;
    xExpression: Expression | undefined;
  };
  primaryLineType?: PrimaryLineType;
  query: Query;
  runsLinePlotConfig: RunsLinePlotConfig;
  useCappedSystemMetrics?: boolean;
  xStepRange: Range | null;
}): RunsDataQueryConfig {
  const {
    defaultMaxRuns,
    dynamicConfigGroupingDepth,
    enableBackendSmoothing = false,
    isFullFidelity = false,
    parsedExpressions,
    primaryLineType = 'full-fidelity',
    query,
    runsLinePlotConfig,
    xStepRange,
  } = options;
  const singleRun = Boolean(query.runName);
  const isQueryConfigGrouped =
    PanelHelpers.isQueryConfigGrouped(runsLinePlotConfig);
  const hasAGroupedRunset = isAnyRunsetGrouped(query.runSets ?? []);

  /**
   * Limit how many metrics we process in multi-metric charts.
   *
   * This value is used in two places that must stay in sync:
   * 1. Run filtering: We filter to runs that have at least one of the selected metrics.
   *    If we filter on more metrics than we display, we'd include runs with no visible data.
   * 2. History specs: We request history data for each metric. The number of metrics
   *    requested must match what we filtered for, otherwise the data won't align.
   *
   * The limit ensures we don't overwhelm the backend with too many metric queries,
   * capped at MULTI_METRIC_MAX_RUNS (200) as a hard maximum.
   */
  const effectiveMetricLimit =
    runsLinePlotConfig.metricLimit != null
      ? Math.min(
          Math.max(1, runsLinePlotConfig.metricLimit),
          MULTI_METRIC_MAX_RUNS
        )
      : MULTI_METRIC_MAX_RUNS;

  let result;
  if (singleRun) {
    result = toRunsDataQueryConfig(query, undefined, {
      panelType: 'Run History Line Plot',
    });
  } else {
    const queryToDataQueryParams: QueryToRunsDataQueryParams = {};

    if (
      runsLinePlotConfig.metrics != null &&
      runsLinePlotConfig.metrics.length > 0
    ) {
      // Filter to runs that have at least one of the selected metrics
      const filters: Array<Filter<Key>> = runsLinePlotConfig.metrics
        .slice(0, effectiveMetricLimit)
        .map(metric => ({
          key: {section: 'keys_info', name: metric},
          op: '!=',
          value: null,
        }));
      queryToDataQueryParams.mergeFilters = Filters.Or(filters);
    }

    result = toRunsDataQueryConfig(query, queryToDataQueryParams, {
      panelType: 'Run History Line Plot',
    });
  }

  if (runsLinePlotConfig.useRunsTableGroupingInPanels === false) {
    result.runsetQueries = result.runsetQueries.map(q => ({
      ...q,
      grouping: [],
    }));
  }

  result.disabled = true;

  result.xAxisScale = runsLinePlotConfig.xLogScale ? 'log' : undefined;
  result.excludeOutliers = areOutliersExcluded(
    runsLinePlotConfig.excludeOutliers
  );
  result.groupingDepth =
    isFullFidelity &&
    Number.isFinite(dynamicConfigGroupingDepth) &&
    Number.isFinite(runsLinePlotConfig.groupingDepth)
      ? 1
      : undefined;

  const extraFields = getExtraFields(
    runsLinePlotConfig,
    parsedExpressions,
    query
  );

  result.configKeys = extraFields
    .filter(key => key.section === 'config')
    .map(key => {
      // If the key has an array index, we only want the base name, and we'll deal with the nesting in the legend
      if (shouldAddDotValue(key)) {
        return keyPossibilities(key).map(k => k.name.split('[')?.[0]);
      }
      return key.name.split('[')?.[0];
    })
    .flat();

  result.summaryKeys = extraFields
    .filter(key => key.section === 'summary')
    .map(key => key.name);

  if (runsLinePlotConfig.metrics != null) {
    // We load the history data for the graphs
    // We sample values where an individual metric and the xAxis has values
    // Some users might prefer to sample history rows where all of the metrics
    // and the xAxis has a value, but this will cause some graphs to not display
    // if for example a user logs test data in one step and training data in another
    // and then wants to plot the two metrics in a single graph.
    result.disabled = false;
    result.history = true;

    const xAxis = runsLinePlotConfig.xAxis ?? DEFAULT_X_AXIS_SETTINGS.xAxis;

    const yAxisExprKeys: string[] = [];
    for (const expr of parsedExpressions.expressions ?? []) {
      yAxisExprKeys.push(...metricsInExpression(expr));
    }
    const xAxisExprKeys: string[] =
      parsedExpressions.xExpression != null
        ? metricsInExpression(parsedExpressions.xExpression)
        : [];

    const xAxisHistoryKeys: string[] = []; // history keys we need besides the metric
    if (!getHasSystemMetrics(runsLinePlotConfig)) {
      xAxisHistoryKeys.push('_step');
    }
    if (xAxis === '_absolute_runtime') {
      // we calculate absolute wall time from the start of the run
      // we need both timestamp and runtime to do this
      xAxisHistoryKeys.push('_timestamp');
      xAxisHistoryKeys.push('_runtime');
    } else {
      // this is the normal case
      xAxisHistoryKeys.push(xAxis);
    }
    xAxisHistoryKeys.push(...xAxisExprKeys);

    let numberOfPoints = CHART_SAMPLES;
    if (isFullFidelity) {
      // This will be set later by useBucketedData
      // if we're capping system metrics be more aggressive about throttling
      // TODO: keep this from being overwritten downstream
      numberOfPoints = options.useCappedSystemMetrics ? 250 : 1000;
    }
    if (xAxis === '_step') {
      runsLinePlotConfig.xAxis = xAxis;
    }

    const limitedMetrics = runsLinePlotConfig.metrics.slice(
      0,
      effectiveMetricLimit
    );

    // Expression keys are always included - they represent explicit user intent
    const specs = _.uniq([
      ...limitedMetrics,
      ...yAxisExprKeys,
      ...xAxisExprKeys,
    ]).map(metricKey =>
      createGorillaHistorySpec({
        enableBackendSmoothing,
        isQueryConfigGrouped,
        metricKey,
        numberOfPoints,
        runsLinePlotConfig,
        useGorillaOutliers: isFullFidelity,
        xAxis,
        xAxisHistoryKeys,
        xStepRange,
      })
    );

    if (isFullFidelity) {
      result.bucketedHistorySpecs = specs as BucketedHistorySpec[];
      result.primaryLineType = primaryLineType;
    } else {
      result.historySpecs = specs as HistorySpec[];
    }

    const maxRunsForMetricCount =
      !singleRun && !isFullFidelity && specs.length > 1
        ? Math.max(1, Math.floor(MULTI_METRIC_MAX_RUNS / specs.length))
        : undefined;

    /**
     * order of specificity:
     * 1. if an explicit limit is on the config (such as if a user has set the max runs value in the UI), use that
     * 2. otherwise default to the defaultMaxRuns value which can vary by custom default.
     * 3. But we shouldn't let the user set a limit that's greater than the defaultMaxRuns value.
     *
     * For sampled multi-metric panels we also cap by metric count, matching the
     * total-line limit used in the bucketed path. Regex metric selection can
     * expand to 100 metrics, and allowing defaultMaxRuns for every metric makes
     * RunsStateDeltaQuery large enough to time out.
     */
    result.page = {
      size: singleRun
        ? 1
        : Math.min(
            runsLinePlotConfig.limit ?? defaultMaxRuns,
            defaultMaxRuns,
            maxRunsForMetricCount ?? defaultMaxRuns
          ),
    };

    /**
     * Rules to use the grouping limit vs the default limit.
     * Config switches:
     * hasAGroupedRunset: there's a grouping rule active in the run selector
     * useRunsTableGroupingInPanels: whether the panel will group based on run selector rules (default is undefined)
     * isQueryConfigGrouped: if the panel itself has its own grouping rules
     * So, grouping is active when -
     * 1. There is a grouped runset AND the user has runs table grouping enabled in the panels
     * 2. There is an active query config in the panel
     *
     * TO RESEARCH: using multiple runsets in a report complicates this. We need to investigate those permutations.
     */

    // since `undefined` here is the same as true, any value not explicitly `false` is the same as `true`
    const useRunsTableGroupingInPanels =
      runsLinePlotConfig.useRunsTableGroupingInPanels !== false;

    if (
      isQueryConfigGrouped ||
      (hasAGroupedRunset && useRunsTableGroupingInPanels)
    ) {
      // We need the metadata for grouping because we do it locally
      // TODO: move grouping to server
      // result.disableMeta = false;

      // optionally compute group statistics over all runs instead of sub-sampling
      // Preserve the multi-metric throttle here too. Otherwise grouped panels
      // overwrite the earlier cap and broad regexes can still time out RSDQ.
      result.page.size = Math.min(
        runsLinePlotConfig.groupRunsLimit ?? DEFAULT_LINE_PLOT_MAX_GROUP_RUNS,
        maxRunsForMetricCount ?? DEFAULT_LINE_PLOT_MAX_GROUP_RUNS
      );
      // Disable grouping for this query, we'll do it ourselves.
      result.runsetQueries = result.runsetQueries.map(q => ({
        ...q,
        grouping: [],
        maxGroups: runsLinePlotConfig.limit,
      }));
    }
  }

  return result;
}

type CreateGorillaHistorySpecParams = {
  enableBackendSmoothing?: boolean;
  isQueryConfigGrouped?: boolean;
  metricKey: string;
  numberOfPoints: number;
  runsLinePlotConfig?: RunsLinePlotConfig;
  useGorillaOutliers: boolean;
  xAxis: string;
  xAxisHistoryKeys: string[];
  xStepRange: Range | null;
};

export function createGorillaHistorySpec({
  enableBackendSmoothing,
  isQueryConfigGrouped,
  metricKey,
  numberOfPoints,
  runsLinePlotConfig,
  useGorillaOutliers,
  xAxis,
  xAxisHistoryKeys,
  xStepRange,
}: CreateGorillaHistorySpecParams): HistorySpec | BucketedHistorySpec {
  let throttledSampledGroupLines = numberOfPoints;

  // If we're grouping and in sampling, we allow a larger number of runs that usual
  // this creates backend load so we're going to ramp down the number of points per
  // run to compensate
  if (isQueryConfigGrouped && !useGorillaOutliers) {
    throttledSampledGroupLines = Math.min(1000, throttledSampledGroupLines);
  }

  const spec = {
    /**
     * NOTE: if the yAxis metric is not the FIRST key, backend lines functionality will break this is because we specify the xAxis metric in the spec definition but we do not similarly specify the yAxis metric. Certain conditions can make inferring the yAxis metric difficult e.g. if _step is always included it's hard to disambiguate plotting _globalStep vs _step vs global_step vs global_step sinceh the specs after uniq() will be [global_step, _step]
     */
    keys: _.uniq([metricKey, ...xAxisHistoryKeys]),
    samples: throttledSampledGroupLines,
    // The API won't accept zoom ranges in decimal form - they must be integers, so make sure to round
    ...(xStepRange != null
      ? {
          minStep: Number.isFinite(xStepRange.min)
            ? // @ts-expect-error TS doesn't handle this check correctly
              Math.floor(xStepRange.min)
            : undefined,
          maxStep: Number.isFinite(xStepRange.max)
            ? // @ts-expect-error TS doesn't handle this check correctly
              Math.ceil(xStepRange.max)
            : undefined,
        }
      : {}),
  };

  if (useGorillaOutliers) {
    /**
     * Custom x-axes (including _absolute_runtime) use minX/maxX instead of minStep/maxStep,
     * which are reserved for _step and require integer values.
     */
    const notASupportedCustomMetric = xAxis === '_step';

    const customXRange = notASupportedCustomMetric
      ? {}
      : {
          minStep: undefined,
          maxStep: undefined,
          minX:
            xStepRange && Number.isFinite(xStepRange.min)
              ? (xStepRange.min ?? undefined)
              : undefined,
          maxX:
            xStepRange && Number.isFinite(xStepRange.max)
              ? (xStepRange.max ?? undefined)
              : undefined,
        };

    return {
      ...spec,
      ...customXRange,
      bins: Math.floor(numberOfPoints),
      xAxis,
      metricTransformations:
        runsLinePlotConfig != null && enableBackendSmoothing
          ? getMetricTransformations(runsLinePlotConfig, metricKey)
          : undefined,
      groupAgg: isQueryConfigGrouped ? runsLinePlotConfig?.groupAgg : undefined,
      groupArea: isQueryConfigGrouped
        ? runsLinePlotConfig?.groupArea
        : undefined,
    };
  }
  return spec;
}
export function defaultTitle(
  runsLinePlotConfig: Pick<RunsLinePlotConfig, 'metrics' | 'expressions'>
): string {
  // eslint-disable-next-line no-extra-boolean-cast
  const metricsInUse = Boolean(runsLinePlotConfig.expressions?.[0])
    ? runsLinePlotConfig.expressions
    : runsLinePlotConfig.metrics;

  return metricsInUse?.join(', ') ?? '';
}

export function getHasSystemMetrics(
  runsLinePlotConfig: Pick<RunsLinePlotConfig, 'metrics'>
): boolean {
  if (runsLinePlotConfig.metrics == null) {
    return false;
  }
  return runsLinePlotConfig.metrics.some(isSystemMetric);
}

export function getTitleFromConfig(
  runsLinePlotConfig: RunsLinePlotConfig
): string {
  return runsLinePlotConfig.chartTitle || defaultTitle(runsLinePlotConfig);
}
